import { mutationOptions } from "@tanstack/react-query";
import { CHANGE_EMAIL_VERSION_2, UPD_USER_INF } from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { sha1 } from "~/lib/crypto";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { EVENT_NAMES, trackEvent } from "~/lib/tracking";
import { uploadAvatar } from "~/lib/uploadImage";
import { useAuthStore } from "~/stores/useAuthStore";
import type { User } from "~/types/user";
import { toGender } from "~/types/user";
import type {
  ChangeEmailFormValues,
  ChangeEmailResponse,
  GetUserInfoResponse,
  UpdateUserInfoRequest,
  UpdateUserInfoResponse,
} from "./types";

// myProfileQueryOptions / updateCallWaitingMutationOptions は lib/queryOptions に移動した
// （複数 feature・route loader・共有フックから参照されるため）。既存 import 互換のため re-export する。
export {
  myProfileQueryOptions,
  updateCallWaitingMutationOptions,
} from "~/lib/queryOptions/myProfile";

const toUser = (data: GetUserInfoResponse): User => {
  return {
    id: data.user_id,
    name: data.user_name,
    email: data.email,
    gender: toGender(data.gender),
    age: data.age,
    avatarUrl: data.ava_id ?? null,
    region: data.region ?? null,
  };
};

export const updateUserInfoMutationOptions = mutationOptions({
  mutationFn: (data: UpdateUserInfoRequest) =>
    apiClient<UpdateUserInfoResponse>(
      UPD_USER_INF,
      data as Record<string, unknown>,
    ),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
    trackEvent(EVENT_NAMES.EDIT_PROFILE);
  },
});

export const changeEmailMutationOptions = mutationOptions({
  mutationFn: async (data: ChangeEmailFormValues) => {
    const hashedNewPwd = await sha1(data.newPassword);
    return apiClient<ChangeEmailResponse>(CHANGE_EMAIL_VERSION_2, {
      email: data.email,
      original_pwd: data.currentPassword,
      new_pwd: hashedNewPwd,
    });
  },
  onSuccess: (data) => {
    queryClient.setQueryData(queryKeys.myProfile.detail(), data);
    useAuthStore.getState().setUser(toUser(data));
  },
});

export const uploadAvatarMutationOptions = mutationOptions({
  mutationFn: (blob: Blob) => uploadAvatar(blob),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
    trackEvent(EVENT_NAMES.UPLOAD_PROFILE_AVATAR);
  },
});
